import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-8" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="border-b border-border/50 pb-8">
            <h1
              data-testid="terms-title"
              className="font-['Sora'] text-3xl sm:text-4xl font-bold text-foreground tracking-tight"
            >
              Terms & Conditions
            </h1>
            <p className="text-primary font-medium mt-2">DDesign | Creative Studio</p>
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
              These Terms & Conditions outline the agreement between DDesign | Creative Studio and the client for the provision of design services. Approval of this proposal confirms acceptance of the terms stated below.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">1. Scope of Work</h2>
              <div className="text-muted-foreground text-sm leading-relaxed space-y-3">
                <p>All services will be delivered in accordance with the project details agreed upon prior to commencement. This includes the confirmed deliverables, timeline, and total project investment as outlined in this proposal and any written communication between both parties.</p>
                <p>Any additional work requested outside the agreed scope will be quoted and billed separately.</p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">2. Payment Terms</h2>
              <ul className="text-muted-foreground text-sm leading-relaxed space-y-2 list-disc list-inside">
                <li>A 50% non-refundable deposit is required to secure the project and schedule work.</li>
                <li>The remaining 50% balance is payable upon project completion and before the release of final files and transferable rights.</li>
                <li>DDesign | Creative Studio reserves the right to pause or withhold work in the event of overdue payments.</li>
                <li>Payments are accepted via bank transfer, PayPal, or other agreed methods.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">3. Revisions</h2>
              <ul className="text-muted-foreground text-sm leading-relaxed space-y-2 list-disc list-inside">
                <li>Each project includes two (2) revision rounds unless otherwise stated.</li>
                <li>Additional revisions will be quoted and billed separately.</li>
                <li>Revision requests must be submitted within 7 days of receiving the design preview.</li>
                <li>Any revisions requested after final file delivery will be treated as a new project or billed separately.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">4. Project Timeline & Delivery</h2>
              <ul className="text-muted-foreground text-sm leading-relaxed space-y-2 list-disc list-inside">
                <li>The project timeline will be determined based on the scope and complexity of the work and will be confirmed prior to commencement.</li>
                <li>Timely feedback and content delivery from the client are required to maintain the agreed schedule. Delays in communication or missing materials may result in an adjusted timeline.</li>
                <li>If the client becomes unresponsive for more than 14 days, the project may be placed on hold. A revised timeline and/or continuation fee may apply.</li>
                <li>Final files will be supplied in the agreed formats (e.g. PNG, JPG, PDF, PSD).</li>
                <li>Rush projects may be subject to an additional fee.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">5. Cancellation & Refund Policy</h2>
              <ul className="text-muted-foreground text-sm leading-relaxed space-y-2 list-disc list-inside">
                <li>The initial deposit is non-refundable once work has commenced.</li>
                <li>If the project is cancelled before work begins, a partial refund may be issued at the discretion of DDesign | Creative Studio.</li>
                <li>If the project is cancelled after work has started, the client will be invoiced for all work completed up to that point.</li>
                <li>No refunds will be issued after the delivery of final approved files.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">6. Client Responsibilities</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                The client agrees to provide all necessary content, references, approvals, and feedback in a timely manner to ensure the project proceeds according to schedule.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">7. Copyright & Usage Rights</h2>
              <ul className="text-muted-foreground text-sm leading-relaxed space-y-2 list-disc list-inside">
                <li>All preliminary concepts and unused designs remain the property of DDesign | Creative Studio.</li>
                <li>Upon full payment, the client is granted the right to use the final approved design for its intended purpose.</li>
                <li>The client may not resell, modify, or redistribute the design without prior written consent.</li>
                <li>DDesign | Creative Studio retains the right to showcase completed work in its portfolio, website, and social media channels unless otherwise agreed in writing.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">8. Third-Party Assets</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Any third-party costs such as fonts, stock images, domains, hosting, printing, or external services are not included unless explicitly stated in the proposal.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">9. Confidentiality</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                All client information, project content, and discussions will be treated as confidential and will not be shared with third parties without written permission.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">10. Communication</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                All official project communication will take place via the agreed channels. Typical response time is within 24–48 business hours.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">11. Limitation of Liability</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                DDesign | Creative Studio shall not be held liable for any damages, losses, or claims arising from the use of the final delivered designs.
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">12. Force Majeure</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                DDesign | Creative Studio shall not be held responsible for delays caused by circumstances beyond its control, including but not limited to illness, technical failure, natural events, or other unforeseen situations.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-border/50 pt-8 mt-12">
            <p className="text-xs text-muted-foreground">
              Last updated: January 2026
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
